// grid-toolbar.ts — the grid-side control bar (seam grid-toolbar: R26, R28,
// R30-R33). The owner's point is that the Bridge release grid and WaaG in
// the Helm MATCH, so the roster is DATA — one list drives the render, the
// acceptance tests and (wave 4, Q15) the Users/Audit copies — rather than
// four hand-rolled template branches that can drift one button at a time.
//
// Geometry: .hc-gtbtn (styles.css, grid-toolbar anchor) is spec'd to WaaG's
// .gl-ibtn — 28px box, 14px glyph, radius 6, surface-2 fill — a DELIBERATE
// decision over re-speccing .hc-iconbtn (30px/17px/radius 8, transparent),
// whose shape every existing shell control wears. The two surfaces the
// owner asked to match are not left 2px apart, and the shell's own controls
// are not silently re-shaped either.
//
// Tooltips (R33) ride the shared layer via data-tip, never native title=
// (tooltip.ts's own header: slow to appear and unstylable). The console's
// listener is mounted app-wide on #app by app.ts (design-system-foundation);
// these buttons only have to carry the attribute.

import { icon } from './generated/icons.js';
import { iconBtn } from './buttons.js';

export interface GridToolbarButton {
  /** DOM id — #hcColsBtn/#hcCreate are carried forward BY NAME (the e2e
   * specs and the existing wiring bind them; #372 parked them for this). */
  id: string;
  /** Sprite glyph (icon-roster's inventory; R27 pinned panels-top-left). */
  glyph: string;
  /** data-tip text (R33) — doubles as the aria-label on icon-only buttons. */
  tip: string;
  /** THE additive action of the bar — the create verb keeps reading as the
   * one thing this toolbar makes. Rendered with the primitive's `additive`
   * intent under the 'release-grid-toolbar' view context (at most one). */
  primary?: boolean;
  /** Rides inside .hc-colswrap with the #hcColsMenu popover holder. */
  wrap?: 'cols';
}

/** Left-to-right roster. Order MATCHES WaaG (#glCols then #glReset, then
 * the #glRefresh this seam adds there) — Columns, Reset, Refresh — with
 * Create appended on the Bridge, the one surface that can create. */
export const GRID_TOOLBAR_BUTTONS: readonly GridToolbarButton[] = [
  { id: 'hcColsBtn', glyph: 'panels-top-left', tip: 'Choose which columns are shown', wrap: 'cols' },
  { id: 'hcReset', glyph: 'list-restart', tip: 'Reset columns, filters and pins to default' },
  { id: 'hcRefresh', glyph: 'refresh-cw', tip: 'Refresh the release grid now' },
  { id: 'hcCreate', glyph: 'plus', tip: 'Create release', primary: true },
];

/** The admin pages' subset (users-audit-parity, ruling Q15 default):
 * Columns + Reset + Refresh land in both pages' existing .hc-toolbar rows;
 * Create stays the home grid's — Users keeps 'Add user' as its own primary
 * action, and Audit creates nothing. ONE list, so the two admin pages and
 * their acceptance test cannot drift button by button. */
export const ADMIN_GRID_TOOLBAR: readonly string[] = ['hcColsBtn', 'hcReset', 'hcRefresh'];

function renderButton(b: GridToolbarButton): string {
  if (b.wrap === 'cols') {
    // The column picker's control OPENS #hcColsMenu. S×T W2 (2026-09-08): it
    // rides the primitive like the three buttons beside it, aria-haspopup
    // through attrs. Stamped as a deferred trigger it wore `hc-gtbtn` ALONE,
    // and that class is layout-only, so it rendered the browser's default
    // outset chrome next to three ramp-painted siblings (measured in the
    // parity probe's bridge-grid screenshot).
    const btn = iconBtn({ glyph: b.glyph, label: b.tip, size: 'sm', id: b.id, cls: 'hc-gtbtn', attrs: { 'aria-haspopup': 'menu' } });
    return `<div class="hc-colswrap">${btn}<div class="hc-colsmenu" id="hcColsMenu" hidden role="menu" aria-label="Column picker"></div></div>`;
  }
  // Styles × Themes PR-2: the roster's action buttons ride the primitive.
  // `hc-gtbtn` STAYS in cls — it is this seam's 28px geometry AND the
  // anchor of `.hc-gtbtn.hc-spinning .icon` (the refresh spin four pages
  // wire by class: home, users, audit, config-templates). `hc-gtbtn-primary`
  // is GONE: the create verb reads as THE action from the additive ramp now.
  // NOTE for the CSS pass: .hc-gtbtn still declares background/color/border,
  // which shadows the --btn-* ramp on these buttons until it is reduced to
  // layout-only.
  if (b.primary) {
    return iconBtn({
      glyph: b.glyph, label: b.tip, intent: 'additive', ctx: 'release-grid-toolbar',
      size: 'sm', id: b.id, cls: 'hc-gtbtn',
    });
  }
  return iconBtn({ glyph: b.glyph, label: b.tip, size: 'sm', id: b.id, cls: 'hc-gtbtn' });
}

/** Render a roster subset as bare buttons — the admin pages inject these
 * into their EXISTING .hc-toolbar rows (they already carry a search box and,
 * on Audit, the query selects; a second full-width row would double the
 * chrome). `tips` overrides per id: 'Refresh the release grid now' is the
 * home surface's sentence, not the user list's. The roster stays the ONE
 * source — ids, glyphs and order come from it, never a second list. */
export function renderGridToolbarButtons(
  opts: { include?: readonly string[]; tips?: Partial<Record<string, string>> } = {},
): string {
  const keep = opts.include ? new Set(opts.include) : null;
  return GRID_TOOLBAR_BUTTONS
    .filter((b) => !keep || keep.has(b.id))
    .map((b) => {
      const tip = opts.tips?.[b.id];
      return renderButton(tip ? { ...b, tip } : b);
    })
    .join('');
}

/** The toolbar row: DOWN by the grid (R26/R30 — directly above it, the WaaG
 * placement), buttons pushed flush right by the spacer exactly as WaaG's
 * .gl-spring pushes #glCols/#glReset. Reuses .hc-toolbar — the admin pages'
 * structurally identical row — so body.hc-fixed already holds its height. */
export function renderGridToolbar(): string {
  return `<div class="hc-toolbar hc-gridbar" id="hcGridBar"><div class="hc-spacer"></div>${
    renderGridToolbarButtons()
  }</div>`;
}

// ---------------------------------------------------------------------------
// THE COLUMN PICKER POPOVER lives in the SHARED client layer now —
// pi-stack/src/client/column-picker.ts — because the Helm's glance grid
// mounts the same popover (owner screenshot 2026-09-09: its rail painted
// behind the card). One implementation on both hosts; this is the console's
// seam onto it, like buttons.ts / tooltip.ts / form-controls.ts.
// ---------------------------------------------------------------------------
export {
  mountColumnPicker, COLUMN_PICKER_MIN_HEIGHT,
  type ColumnPickerEntry, type ColumnPickerOpts, type ColumnPickerCtl,
} from 'pi-stack/client/column-picker.js';
